import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { ChevronsUpDown, PanelLeft } from 'lucide-react'
import { useOrgStore } from '@/stores/orgStore'
import { usePersonaStore } from '@/stores/personaStore'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Separator } from '@/components/ui/separator'
import { useSidebar } from '@/components/ui/sidebar'
import { Ripple } from '../magicui/ripple'

const apiUrl = import.meta.env.VITE_API_URL

interface Persona {
  current_user_level: number
  persona_id: string
  persona_name: string
}

interface ApiResponse {
  limit: number
  personas: Persona[]
  page: number
  total: number
}

const validateAndGetOrgId = (orgId: string | null): string | null => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  try {
    const storedOrgId = localStorage.getItem('currentOrgId')
    const parsedOrgId = storedOrgId ? JSON.parse(storedOrgId) : null
    const finalOrgId = orgId || parsedOrgId
    return finalOrgId && uuidRegex.test(finalOrgId) ? finalOrgId : null
  } catch {
    localStorage.removeItem('currentOrgId')
    return null
  }
}

const usePersonas = () => {
  const { getAccessTokenSilently } = useAuth0()
  const currentOrgId = useOrgStore((state) => state.currentOrgId)
  const validOrgId = validateAndGetOrgId(currentOrgId)

  return useQuery({
    queryKey: ['personas', validOrgId],
    queryFn: async () => {
      if (!validOrgId) return []

      const accessToken = await getAccessTokenSilently()
      const response = await fetch(
        `${apiUrl}/organizations/${validOrgId}/personas?page=1&limit=100`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Cache-Control': 'no-cache',
          },
        }
      )

      if (!response.ok) throw new Error('Failed to fetch personas')
      const data: ApiResponse = await response.json()
      return data.personas
    },
    enabled: !!validOrgId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  })
}

export const SidebarTriggerWithPersona = React.forwardRef<
  React.ElementRef<typeof Button>,
  React.ComponentProps<typeof Button>
>(({ className, onClick, ...props }, ref) => {
  const { toggleSidebar } = useSidebar()
  const { data: personas = [], isLoading } = usePersonas()
  const { setCurrentPersonaId, setCurrentPersonaName } = usePersonaStore()
  const currentOrgId = useOrgStore((state) => state.currentOrgId)
  const [selectedPersona, setSelectedPersona] = React.useState<Persona | null>(
    null
  )

  React.useEffect(() => {
    // Reset persona state when organization changes
    setSelectedPersona(null)
    setCurrentPersonaId('')
    setCurrentPersonaName('')
    localStorage.removeItem('currentPersonaId')
  }, [currentOrgId, setCurrentPersonaId, setCurrentPersonaName])

  React.useEffect(() => {
    if (!personas.length) {
      setSelectedPersona(null)
      return
    }

    const storedId = localStorage.getItem('currentPersonaId')
    const parsedId = storedId ? JSON.parse(storedId) : null
    const target = parsedId
      ? personas.find((p) => p.persona_id === parsedId)
      : personas[0]

    if (target) {
      setSelectedPersona(target)
      setCurrentPersonaId(target.persona_id)
      setCurrentPersonaName(target.persona_name)
    } else {
      setSelectedPersona(personas[0])
      setCurrentPersonaId(personas[0].persona_id)
      setCurrentPersonaName(personas[0].persona_name)
    }
  }, [personas, setCurrentPersonaId, setCurrentPersonaName])

  const updatePersonaState = (persona: Persona) => {
    setSelectedPersona(persona)
    setCurrentPersonaId(persona.persona_id)
    setCurrentPersonaName(persona.persona_name)
    localStorage.setItem('currentPersonaId', JSON.stringify(persona.persona_id))
  }

  if (isLoading) {
    return (
      <div className='fixed inset-0 flex items-center justify-center bg-background z-[9999]'>
        <div className='h-full w-full flex items-center justify-center'>
          <Ripple />
        </div>
      </div>
    )
  }

  return (
    <div className='flex items-center space-x-2'>
      <Button
        ref={ref}
        data-sidebar='trigger'
        variant='ghost'
        size='icon'
        className={cn('h-7 w-7', className)}
        onClick={(event) => {
          onClick?.(event)
          toggleSidebar()
        }}
        {...props}
      >
        <PanelLeft />
        <span className='sr-only'>Toggle Sidebar</span>
      </Button>
      <Separator orientation='vertical' className='h-6' />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='ghost'
            size='sm'
            className='flex items-center space-x-2'
            disabled={!personas.length}
          >
            <span>{selectedPersona?.persona_name || 'Nenhuma Persona'}</span>
            <ChevronsUpDown className='ml-2 h-4 w-4' />
          </Button>
        </DropdownMenuTrigger>
        {personas.length > 0 && (
          <DropdownMenuContent align='start' side='bottom'>
            {personas.map((persona) => (
              <DropdownMenuItem
                key={persona.persona_id}
                onSelect={() => updatePersonaState(persona)}
              >
                {persona.persona_name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        )}
      </DropdownMenu>
    </div>
  )
})

SidebarTriggerWithPersona.displayName = 'SidebarTriggerWithPersona'
